import { store } from '@redux/store';
import { personName } from 'constants/regexExpressions';
import { fromEvent } from 'rxjs';
import requestCardVerification from '../apis/userCardVerification';
import ModalStep from '../enums/modalStep';
import VerificationMethod from '../enums/verificationMethod';
import UserStatus from '../enums/userStatus';
import { selectSpecId } from '../redux/selectors';
import { setModalStep, setVerificationMethod } from '../redux/slices';
import { getStoredUserData } from '../services/userService';
import formatPhoneNumber from '../utils/formatPhoneNumber';
import { stripeLogoSvgElement } from '../constants/svgIcons';

/**
 * Stripe element style configuration
 */
const stripeElementStyle = {
  base: {
    '::placeholder': {},
    ':-webkit-autofill': {},
  },
  invalid: {},
};

/**
 * Credit Card Verification component
 * Handles verification of user identity through credit card information
 */
export default class CreditCardVerification {
  // Navigation steps
  nextStep = ModalStep.Questions;

  // RxJS subscriptions
  submitSubscription$ = null;

  validationSubscriptions = [];

  // Stripe elements
  stripeElements = [];

  stripeEmptyMessages = new Map();

  stripeErrorWrappers = new Map();

  // Form validation
  validationErrors = new Map();

  // User information
  user = null;

  /**
   * Creates a new CreditCardVerification instance
   * @param {Object} rootComponent - Parent component that manages modal functionality
   */
  constructor(rootComponent) {
    this.rootComponent = rootComponent;
    this.user = getStoredUserData();
    this.stripe = Stripe(this.rootComponent.stripeApiKey);
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate);
  }

  /**
   * Gets the form element from the DOM
   * @returns {HTMLElement} Form element
   */
  static getFormElement() {
    return document.querySelector('[data-self-guided-tour-credit-card-verification-form]');
  }

  /**
   * Prevents default form submission
   * @param {Event} event - Form submission event
   */
  static submitHandler = (event) => {
    event.preventDefault();
  };

  // =============================================
  // DOM Event Handlers
  // =============================================

  /**
   * Handles DOM updates when the component is displayed or hidden
   * @param {Object} event - DOM update event
   */
  onDomUpdate = (event) => {
    if (event.detail?.step === ModalStep.CreditCardVerification) {
      this.initializeForm();
      // every time this component is shown, we need to get to refresh the user data
      this.user = getStoredUserData();
    } else {
      this.cleanupForm();
    }
  };

  /**
   * Initializes the credit card form and validation
   */
  initializeForm() {
    const formElement = CreditCardVerification.getFormElement();
    // Prevent form submission
    this.submitSubscription$ = fromEvent(formElement, 'submit').subscribe(CreditCardVerification.submitHandler);

    this.setupStripe();
    this.addValidationListeners();
    this.addStripeValidationListeners();
    this.updatePrimaryButtonState();
    this.validateAllFields();
  }

  /**
   * Cleans up subscriptions when the component is hidden
   */
  cleanupForm() {
    this.removeValidationListeners();
    if (this.submitSubscription$) {
      this.submitSubscription$.unsubscribe();
      this.submitSubscription$ = null;
    }
  }

  // =============================================
  // Stripe Integration
  // =============================================

  /**
   * Sets up Stripe Elements for credit card input fields
   */
  setupStripe() {
    const elements = this.stripe.elements();

    // Create and mount Stripe element
    const createAndMountElement = (type, placeholder, wrapper) => {
      const element = elements.create(type, { placeholder, style: stripeElementStyle });
      element.mount(document.querySelector(wrapper));
      return element;
    };

    // Create elements
    const stripeCardNumber = createAndMountElement(
      'cardNumber',
      '1234 **** **** ****',
      '[data-self-guided-tour-credit-card-number-wrapper]'
    );

    const stripeCardExpiry = createAndMountElement(
      'cardExpiry',
      'MM/YY',
      '[data-self-guided-tour-credit-card-expire-wrapper]'
    );

    const stripeCardCvc = createAndMountElement('cardCvc', '000', '[data-self-guided-tour-credit-card-cvc-wrapper]');

    // Store elements for later use
    this.stripeElements = [stripeCardNumber, stripeCardExpiry, stripeCardCvc];

    // Configure error messages
    this.stripeEmptyMessages = new Map([
      [stripeCardNumber, 'Card number is incomplete'],
      [stripeCardExpiry, 'Enter the expiration date'],
      [stripeCardCvc, 'Enter the CVC'],
    ]);

    // Configure error selectors
    this.stripeErrorWrappers = new Map([
      [stripeCardNumber, '[data-self-guided-tour-card-number-error]'],
      [stripeCardExpiry, '[data-self-guided-tour-card-expire-error]'],
      [stripeCardCvc, '[data-self-guided-tour-card-cvc-error]'],
    ]);

    // Initialize all elements as invalid
    this.stripeElements.forEach((element) => {
      this.validationErrors.set(element, this.stripeEmptyMessages.get(element));
    });
  }

  // =============================================
  // Form Validation
  // =============================================

  /**
   * Adds validation listeners to form fields
   */
  addValidationListeners() {
    const validationFields = [
      { id: 'firstname', validate: this.validateFirstName },
      { id: 'lastname', validate: this.validateLastName },
      { id: 'billingaddress', validate: this.validateBillingAddress },
      { id: 'city', validate: this.validateCity },
      { id: 'state', validate: this.validateState },
      { id: 'zipcode', validate: this.validateZipCode },
      { id: 'verify18', validate: this.validateVerify18 },
    ];

    validationFields.forEach(({ id, validate }) => {
      const inputElement = document.getElementById(id);
      if (inputElement) {
        this.addElementValidators(inputElement, validate);
      }
    });
  }

  /**
   * Adds event listeners for a specific input element
   * @param {HTMLElement} element - Input element
   * @param {Function} validateFn - Validation function
   */
  addElementValidators(element, validateFn) {
    // Input event - validate silently
    const inputSub = fromEvent(element, 'input').subscribe(() => {
      validateFn.call(this, element);
      this.updatePrimaryButtonState();
    });

    // Blur event - validate and show errors
    const blurSub = fromEvent(element, 'blur').subscribe(() => {
      validateFn.call(this, element);
      this.showValidationError(element);
      this.updatePrimaryButtonState();
    });

    this.validationSubscriptions.push(inputSub, blurSub);

    // Add change event for select elements
    if (element.tagName === 'SELECT') {
      const changeSub = fromEvent(element, 'change').subscribe(() => {
        validateFn.call(this, element);
        this.updatePrimaryButtonState();
      });
      this.validationSubscriptions.push(changeSub);
    }
  }

  /**
   * Adds validation listeners to Stripe elements
   */
  addStripeValidationListeners() {
    this.stripeElements.forEach((element) => {
      // Change event
      const changeSub = fromEvent(element, 'change').subscribe((event) => {
        this.validateStripeElement(element, event);
        this.updatePrimaryButtonState();
      });

      // Blur event - show errors
      const blurSub = fromEvent(element, 'blur').subscribe(() => {
        this.showStripeValidationError(element);
        this.updatePrimaryButtonState();
      });

      this.validationSubscriptions.push(changeSub, blurSub);
    });
  }

  /**
   * Validates a Stripe element based on its event data
   * @param {Object} element - Stripe element
   * @param {Object} event - Stripe change event
   */
  validateStripeElement(element, event) {
    if (!event.error && !event.empty) {
      this.validationErrors.delete(element);
      return;
    }

    if (event.error) {
      this.validationErrors.set(element, event.error.message);
      return;
    }

    if (event.empty) {
      this.validationErrors.set(element, this.stripeEmptyMessages.get(element));
    }
  }

  /**
   * Removes all validation listeners
   */
  removeValidationListeners() {
    this.validationSubscriptions.forEach((sub) => sub.unsubscribe());
    this.validationSubscriptions = [];
  }

  // =============================================
  // Field Validators
  // =============================================

  /**
   * Validates the first name input
   * @param {HTMLElement} input - First name input element
   */
  validateFirstName = (input) => {
    if (!input.value.trim()) {
      this.validationErrors.set(input, 'Enter your first name');
      return;
    }
    if (!personName.test(input.value.trim())) {
      this.validationErrors.set(input, 'Enter a valid first name');
      return;
    }
    this.validationErrors.delete(input);
  };

  /**
   * Validates the last name input
   * @param {HTMLElement} input - Last name input element
   */
  validateLastName = (input) => {
    if (!input.value.trim()) {
      this.validationErrors.set(input, 'Enter your last name');
      return;
    }
    if (!personName.test(input.value.trim())) {
      this.validationErrors.set(input, 'Enter a valid last name');
      return;
    }
    this.validationErrors.delete(input);
  };

  /**
   * Validates the billing address input
   * @param {HTMLElement} input - Billing address input element
   */
  validateBillingAddress = (input) => {
    if (!input.value.trim()) {
      this.validationErrors.set(input, 'Enter a valid billing address');
      return;
    }
    this.validationErrors.delete(input);
  };

  /**
   * Validates the city input
   * @param {HTMLElement} input - City input element
   */
  validateCity = (input) => {
    if (!input.value.trim()) {
      this.validationErrors.set(input, 'Enter a valid city');
      return;
    }
    this.validationErrors.delete(input);
  };

  /**
   * Validates the state select element
   * @param {HTMLElement} select - State select element
   */
  validateState = (select) => {
    if (!select.value) {
      this.validationErrors.set(select, 'Select a state');
      return;
    }
    this.validationErrors.delete(select);
  };

  /**
   * Validates the ZIP code input
   * @param {HTMLElement} input - ZIP code input element
   */
  validateZipCode = (input) => {
    if (!input.value.trim()) {
      this.validationErrors.set(input, 'Enter ZIP code');
      return;
    }
    this.validationErrors.delete(input);
  };

  /**
   * Validates the age verification checkbox
   * @param {HTMLElement} input - Age verification checkbox
   */
  validateVerify18 = (input) => {
    if (!input.checked) {
      this.validationErrors.set(input, 'You must verify you are 18 years old');
      return;
    }
    this.validationErrors.delete(input);
  };

  // =============================================
  // Error Display
  // =============================================

  /**
   * Shows validation error for a form element
   * @param {HTMLElement} element - Form element
   */
  showValidationError = (element) => {
    const error = this.validationErrors.get(element);
    const parentWrapper = element.parentElement;
    const errorSpan = parentWrapper.querySelector('[data-self-guided-tour-input-error]');
    if (error && errorSpan) {
      errorSpan.textContent = error;
      parentWrapper.classList.add('nhs-c-input__wrapper--error');
    } else if (errorSpan) {
      parentWrapper.classList.remove('nhs-c-input__wrapper--error');
    }
  };

  /**
   * Shows validation error for a Stripe element
   * @param {Object} element - Stripe element
   */
  showStripeValidationError = (element) => {
    const error = this.validationErrors.get(element)?.replace(/\.$/, '');
    const errorSelector = this.stripeErrorWrappers.get(element);
    const errorSpan = document.querySelector(errorSelector);
    const parentWrapper = errorSpan.parentElement;

    if (error && errorSpan) {
      errorSpan.textContent = error;
      parentWrapper.classList.add('nhs-c-input__wrapper--error');
    } else if (errorSpan) {
      parentWrapper.classList.remove('nhs-c-input__wrapper--error');
    }
  };

  /**
   * Checks if the form is valid
   * @returns {boolean} True if form is valid
   */
  getFormInputIsValid() {
    return this.validationErrors.size === 0;
  }

  /**
   * Validates all form fields without showing error messages
   * Returns true if all fields pass validation, false otherwise
   * @returns {boolean} Form validity status
   */
  validateAllFields() {
    // First, clear any existing errors to ensure a clean validation state.
    // We'll preserve the original errors to restore them later
    const originalErrors = new Map(this.validationErrors);
    this.validationErrors.clear();

    // Validate standard form fields
    const standardFields = [
      { id: 'firstname', validate: this.validateFirstName },
      { id: 'lastname', validate: this.validateLastName },
      { id: 'billingaddress', validate: this.validateBillingAddress },
      { id: 'city', validate: this.validateCity },
      { id: 'state', validate: this.validateState },
      { id: 'zipcode', validate: this.validateZipCode },
      { id: 'verify18', validate: this.validateVerify18 },
    ];

    // Run validation on each field
    standardFields.forEach(({ id, validate }) => {
      const element = document.getElementById(id);
      if (element) {
        validate.call(this, element);
      }
    });

    // Check Stripe elements
    // If we have any stored errors for Stripe elements, keep them
    this.stripeElements.forEach((element) => {
      if (originalErrors.has(element)) {
        this.validationErrors.set(element, originalErrors.get(element));
      }
    });

    // // If any validation errors were found, the form is invalid
    return this.validationErrors.size === 0;
  }

  /**
   * Updates the state of the primary button based on form validity
   */
  updatePrimaryButtonState() {
    const isValid = this.getFormInputIsValid();
    this.rootComponent.setPrimaryButtonDisabled(!isValid);
  }

  // =============================================
  // Form Processing
  // =============================================

  /**
   * Handles primary button click
   * Validates the form and submits if valid
   */
  async handlePrimaryAction() {
    // Double-check form validity
    const valid = this.getFormInputIsValid();
    if (!valid) {
      return;
    }

    try {
      // Get Stripe token
      const token = await this.getStripeToken();

      const state = store.getState();
      const specId = selectSpecId(state);

      // Set User first and last name
      const { firstname, lastname } = CreditCardVerification.getFormData();

      // Update user data with first and last name
      const userData = { ...getStoredUserData() };
      userData.firstName = firstname;
      userData.lastName = lastname;

      // Request card verification using the token and updated user data
      const verification = await requestCardVerification(specId, token, userData);

      if (!verification) {
        throw new Error('Card verification failed');
      }

      // Update user data if verification is successful
      this.rootComponent.onUserChange$.next(userData);

      // Update Redux store with verification method and proceed to the next step
      store.dispatch(setVerificationMethod({ verificationMethod: 'Credit Card' }));
      store.dispatch(setModalStep({ modalStep: this.nextStep }));
    } catch (error) {
      this.rootComponent.showNotification('There was an error validating your card. Please try again.');
    }
  }

  /**
   * Processes form data by creating a Stripe token and sending it to the server
   * @returns {Promise<Object>} Card verification response
   */
  async getStripeToken() {
    const data = CreditCardVerification.getFormData();

    const token = await this.verifyCreditCard(
      data.firstname,
      data.lastname,
      data.billingaddress,
      data.city,
      data.state,
      data.zipcode
    );

    return token;
  }

  static getFormData() {
    const formElement = CreditCardVerification.getFormElement();
    const formData = new FormData(formElement);
    return Object.fromEntries(formData.entries());
  }

  /**
   * Creates a Stripe token for card verification
   * @param {string} firstname - First name
   * @param {string} lastname - Last name
   * @param {string} billingAddress - Billing address
   * @param {string} city - City
   * @param {string} state - State
   * @param {string} zipCode - ZIP code
   * @returns {Promise<string>} Stripe token ID
   */
  verifyCreditCard(firstname, lastname, billingAddress, city, state, zipCode) {
    const data = {
      name: `${firstname} ${lastname}`,
      address_line1: billingAddress,
      address_city: city,
      address_state: state,
      address_zip: zipCode,
      address_country: 'US',
    };

    // Stripe can figure out the other elements created from the same elements instance
    // so we only need to pass the first element
    return this.stripe.createToken(this.stripeElements[0], data).then(({ token, error }) => {
      if (error) {
        throw new Error(error.message);
      }
      return token.id;
    });
  }

  /**
   * Handles secondary button click
   * Returns to previous step
   */
  handleSecondaryAction() {
    this.previousStep = this.getPreviousStep();
    store.dispatch(setModalStep({ modalStep: this.previousStep }));
  }

  /**
   * Gets the previous step based on the current verification methods and user status
   * Returns the previous step
   */
  getPreviousStep() {
    const verificationMethods = this.rootComponent.verificationMethods;
    if (verificationMethods.filter((method) => method.name !== VerificationMethod.Phone.name).length === 2) {
      return ModalStep.IdentityVerification;
    }
    if (verificationMethods.includes(VerificationMethod.Phone)) {
      return ModalStep.PhoneVerification;
    }
    const { name } = this.rootComponent.userStatus;
    const userStatus = UserStatus.fromKey(name);
    if (userStatus === UserStatus.NotFound) {
      return ModalStep.SignUp;
    }
    return ModalStep.SignIn;
  }


  /**
   * Returns the Stripe logo HTML if the previous step is not IdentityVerification.
   * Used to display the Stripe logo in the credit card verification form.
   * @returns {string} HTML string for the Stripe logo or an empty string
   */
  showStripeLogo() {
    const hasCreditCardOnly =
      this.rootComponent.verificationMethods.filter((method) => method !== VerificationMethod.Phone)
        .every((method) => method === VerificationMethod.CreditCard);
    if (hasCreditCardOnly) {
      return `
        <div class="nhs-o-flex-column-items-start-justify-center nhs-u-margin--bottom-600">
          ${stripeLogoSvgElement}
        </div>
      `;
    }
    return "";
  }

  /**
   * Renders the credit card verification form
   * @returns {string} HTML for the component
   */
  render() {
    const { phoneNumber } = this.user;
    const formattedPhoneNumber = phoneNumber.length === 10 ? formatPhoneNumber(phoneNumber) : phoneNumber;

    return `
      <div class="nhs-c-self-guided-left-panel ${this.class}">
        <div>
         <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large">Verify your identity with card</h1>

         <div class="nhs-c-notification__placeholder" data-self-guided-tour-notifications></div>

         <p class="nhs-c-label--black-900-body-1-medium-regular">You may see a temporary charge for identity verification that will be refunded automatically. UTour does not save your card information.
          <br />
          <em class="nhs-u-text-italic">All information is required.</em></p>
        </div>
        
        <form class="nhs-c-wrapper__self-guided-column" data-self-guided-tour-credit-card-verification-form>
          <!-- Name section -->
          <div class="nhs-c-wrapper__inputs--column nhs-mq-wrapper__inputs--row-extra-large">
            <div class="nhs-c-input__wrapper nhs-u-flex--0-1-half">
              <label for="firstname" class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">First name</label>
              <input id="firstname" name="firstname" type="text" class="nhs-c-input__field" minlength="1" maxlength="45" required />
              <span class="nhs-c-input__label--error" data-self-guided-tour-input-error></span>
            </div>

            <div class="nhs-c-input__wrapper nhs-u-flex--0-1-half">
              <label for="lastname" class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">Last name</label>
              <input id="lastname" name="lastname" type="text" class="nhs-c-input__field" minlength="1" maxlength="45" required />
              <span class="nhs-c-input__label--error" data-self-guided-tour-input-error></span>
            </div>
          </div>

          <!-- Phone section -->
          <div class="nhs-c-wrapper__inputs--column nhs-mq-wrapper__inputs--row-extra-large">
            <div class="nhs-c-input__wrapper nhs-u-basis--49">
              <label for="mobilephone" class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">Mobile phone</label>
              <input id="mobilephone" name="mobilephone" type="tel" class="nhs-c-input__field" value="${formattedPhoneNumber}" readonly />
            </div>
          </div>
          
          <!-- Credit card section -->
          <div class="nhs-c-wrapper__inputs--column nhs-mq-wrapper__inputs--row-extra-large">
            <div class="nhs-c-input__wrapper nhs-c-input__wrapper--stripe nhs-u-basis--49">
                <label for="card-number" class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">Credit or Debit card number</label>
                <div id="card-number" class="nhs-c-input__field" data-self-guided-tour-credit-card-number-wrapper></div>
                <span class="nhs-c-input__label--error" data-self-guided-tour-card-number-error>Card number is incomplete</span>
            </div>

            <div class="nhs-c-wrapper__inputs--row--full nhs-mq-wrapper__inputs--row--half-extra-large">
                <div class="nhs-c-input__wrapper nhs-c-input__wrapper--stripe nhs-u-flex--1-1-auto">
                    <label for="card-expdate" class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">Expiration date</label>
                    <div id="card-expdate" class="nhs-c-input__field" data-self-guided-tour-credit-card-expire-wrapper></div>
                    <span class="nhs-c-input__label--error" data-self-guided-tour-card-expire-error>Enter the expiration date</span>
                </div>
                <div class="nhs-c-input__wrapper nhs-c-input__wrapper--stripe nhs-u-basis--third">
                    <label for="card-cvc" class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">CVC</label>
                    <div id="card-cvc" class="nhs-c-input__field" data-self-guided-tour-credit-card-cvc-wrapper></div>
                    <span class="nhs-c-input__label--error" data-self-guided-tour-card-cvc-error>Enter the CVC</span>
                </div>
            </div>
          </div>
          
          <!-- Address section -->
          <div class="nhs-c-wrapper__inputs--column nhs-mq-wrapper__inputs--row-extra-large">
            <div class="nhs-c-input__wrapper nhs-u-flex--0-1-half">
              <label for="billingaddress" class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">Billing address</label>
              <input id="billingaddress" name="billingaddress" type="text" class="nhs-c-input__field" required />
              <span class="nhs-c-input__label--error" data-self-guided-tour-input-error></span>
            </div>

            <div class="nhs-c-input__wrapper nhs-u-flex--0-1-half">
              <label for="city" class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">City</label>
              <input id="city" name="city" type="text" class="nhs-c-input__field" required />
              <span class="nhs-c-input__label--error" data-self-guided-tour-input-error></span>
            </div>
          </div>

          <!-- State and ZIP code section -->
          <div class="nhs-c-wrapper__inputs--column nhs-mq-wrapper__inputs--row-extra-large">
            <div class="nhs-c-wrapper__inputs--row--full nhs-mq-wrapper__inputs--row--flow-extra-large">
              <div class="nhs-c-input__wrapper nhs-u-flex--1-1-auto">
                <label for="state" class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">State</label>
                <select id="state" name="state" class="nhs-c-input__select">
                  <option value="">Select state</option>
                  <option value="AL">Alabama</option>
                  <option value="AK">Alaska</option>
                  <option value="AZ">Arizona</option>
                  <option value="AR">Arkansas</option>
                  <option value="CA">California</option>
                  <option value="CO">Colorado</option>
                  <option value="CT">Connecticut</option>
                  <option value="DE">Delaware</option>
                  <option value="DC">District Of Columbia</option>
                  <option value="FL">Florida</option>
                  <option value="GA">Georgia</option>
                  <option value="HI">Hawaii</option>
                  <option value="ID">Idaho</option>
                  <option value="IL">Illinois</option>
                  <option value="IN">Indiana</option>
                  <option value="IA">Iowa</option>
                  <option value="KS">Kansas</option>
                  <option value="KY">Kentucky</option>
                  <option value="LA">Louisiana</option>
                  <option value="ME">Maine</option>
                  <option value="MD">Maryland</option>
                  <option value="MA">Massachusetts</option>
                  <option value="MI">Michigan</option>
                  <option value="MN">Minnesota</option>
                  <option value="MS">Mississippi</option>
                  <option value="MO">Missouri</option>
                  <option value="MT">Montana</option>
                  <option value="NE">Nebraska</option>
                  <option value="NV">Nevada</option>
                  <option value="NH">New Hampshire</option>
                  <option value="NJ">New Jersey</option>
                  <option value="NM">New Mexico</option>
                  <option value="NY">New York</option>
                  <option value="NC">North Carolina</option>
                  <option value="ND">North Dakota</option>
                  <option value="OH">Ohio</option>
                  <option value="OK">Oklahoma</option>
                  <option value="OR">Oregon</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="RI">Rhode Island</option>
                  <option value="SC">South Carolina</option>
                  <option value="SD">South Dakota</option>
                  <option value="TN">Tennessee</option>
                  <option value="TX">Texas</option>
                  <option value="UT">Utah</option>
                  <option value="VT">Vermont</option>
                  <option value="VA">Virginia</option>
                  <option value="WA">Washington</option>
                  <option value="WV">West Virginia</option>
                  <option value="WI">Wisconsin</option>
                  <option value="WY">Wyoming</option>
                </select>
                <span class="nhs-c-input__label--error" data-self-guided-tour-input-error></span>
              </div>

              <div class="nhs-c-input__wrapper nhs-u-basis--third">
                <label for="zipcode" class="nhs-c-input__label nhs-c-label--black-1000-body-small-regular">ZIP code</label>
                <input id="zipcode" name="zipcode" type="text" class="nhs-c-input__field" required />
                <span class="nhs-c-input__label--error" data-self-guided-tour-input-error></span>
              </div>
            </div>
          </div>

          <!-- Age verification -->
          <div class="nhs-c-input__wrapper">
            <div class="nhs-c-input__container--with-text nhs-u-justify-content-flex-start">
              <input class="nhs-c-input__check-box" data-self-guided-tour-tac-terms-check="" id="verify18" type="checkbox" name="verify18" required />
              <label for="verify18">
                I verify I am 18 years old.
              </label>
            </div>
            <span class="nhs-c-input__label--error" data-self-guided-tour-input-error></span>
          </div>

          ${this.showStripeLogo()}
        </form>
      </div>
    `;
  }
}
